
import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/contexts/AuthContext';
import { Procurement } from '@/types/database.types';
import { 
  getFinanceProcurements, 
  getApprovedFinanceProcurements, 
  getRejectedFinanceProcurements 
} from '@/services/procurement/financeQueryService';

interface UseOptimizedFinanceProcurementsProps {
  status: 'pending' | 'approved' | 'rejected';
  pageSize?: number;
}

export const useOptimizedFinanceProcurements = ({ 
  status, 
  pageSize = 25 
}: UseOptimizedFinanceProcurementsProps) => {
  const { user, userRole } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'created_at', direction: 'desc' });

  const offset = (currentPage - 1) * pageSize;

  // Query function based on status
  const queryFn = useMemo(() => {
    switch (status) {
      case 'pending':
        return () => getFinanceProcurements(
          ['pending_finance', 'pending', 'initiated', 'awaiting_approval'],
          pageSize,
          offset
        );
      case 'approved':
        return () => getApprovedFinanceProcurements(pageSize, offset);
      case 'rejected':
        return () => getRejectedFinanceProcurements(pageSize, offset);
      default:
        return () => getFinanceProcurements([], pageSize, offset);
    }
  }, [status, pageSize, offset]);

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['finance-procurements', status, currentPage, pageSize, user?.id, userRole],
    queryFn: queryFn,
    enabled: !!user,
  });

  // Client-side filtering for search (on current page only)
  const filteredProcurements = useMemo(() => {
    if (!data?.data || !searchTerm) return data?.data || [];
    
    const term = searchTerm.toLowerCase();
    return data.data.filter(p => 
      (p.product_name && p.product_name.toLowerCase().includes(term)) ||
      (p.source_type && p.source_type.toLowerCase().includes(term)) ||
      (p.id && p.id.toLowerCase().includes(term)) ||
      (p.source_name && p.source_name.toLowerCase().includes(term)) ||
      (p.requestor?.first_name && p.requestor.first_name.toLowerCase().includes(term)) ||
      (p.requestor?.last_name && p.requestor.last_name.toLowerCase().includes(term))
    );
  }, [data?.data, searchTerm]);

  // Client-side sorting (on current page only)
  const sortedProcurements = useMemo(() => {
    if (!filteredProcurements) return [];
    
    return [...filteredProcurements].sort((a, b) => {
      if (sortConfig.key === 'created_at') {
        const dateA = new Date(a.created_at).getTime();
        const dateB = new Date(b.created_at).getTime();
        return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
      }
      
      if (sortConfig.key === 'total_amount') {
        const amountA = a.total_amount || 0;
        const amountB = b.total_amount || 0;
        return sortConfig.direction === 'asc' ? amountA - amountB : amountB - amountA;
      }

      if (sortConfig.key === 'requestor') {
        const getRequestorName = (procurement: Procurement) => {
          if (!procurement.requestor) return 'Unknown User';
          const { first_name, last_name } = procurement.requestor;
          if (first_name && last_name) return `${first_name} ${last_name}`;
          return first_name || last_name || 'Unknown User';
        };
        
        const nameA = getRequestorName(a).toLowerCase();
        const nameB = getRequestorName(b).toLowerCase();
        return sortConfig.direction === 'asc' 
          ? nameA.localeCompare(nameB) 
          : nameB.localeCompare(nameA);
      }
      
      if (sortConfig.key === 'payment_due_date') {
        if (!a.finance?.payment_due_date && !b.finance?.payment_due_date) return 0;
        if (!a.finance?.payment_due_date) return sortConfig.direction === 'asc' ? 1 : -1;
        if (!b.finance?.payment_due_date) return sortConfig.direction === 'asc' ? -1 : 1;
        
        const dateA = new Date(a.finance.payment_due_date).getTime();
        const dateB = new Date(b.finance.payment_due_date).getTime();
        return sortConfig.direction === 'asc' ? dateA - dateB : dateB - dateA;
      }
      
      return 0;
    });
  }, [filteredProcurements, sortConfig]);

  const handleSort = (key: string) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const totalPages = Math.ceil((data?.count || 0) / pageSize);

  const goToPage = (page: number) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return {
    procurements: sortedProcurements,
    isLoading,
    error,
    searchTerm,
    setSearchTerm,
    sortConfig,
    handleSort,
    currentPage,
    totalPages,
    totalItems: data?.count || 0,
    pageSize,
    goToPage,
    hasNextPage: currentPage < totalPages,
    hasPreviousPage: currentPage > 1,
    refetch
  };
};
